import { useState, useCallback, useRef } from 'react';
import { userService } from '../../services/api';

/**
 * Hook que carga datos del "otro participante" de cada conversación. En el
 * modelo user-to-user solo necesitamos el nombre/avatar del otro user — no
 * hay productos a hidratar.
 *
 * Devuelve:
 *   users  – { [userId]: userData }
 *   enrichConversations(convList) – call al cambiar la lista
 *   setUsers – permite a callers externos actualizar la cache
 */
const useConversationEnrichment = (currentUserId) => {
  const [users, setUsers] = useState({});
  const loadedUserIds = useRef(new Set());

  const enrichConversations = useCallback(async (convList) => {
    if (!convList || convList.length === 0) return;

    const allUserIds = [...new Set(
      convList.flatMap((c) => Array.isArray(c.participants) ? c.participants : [])
        .map(String)
        .filter((id) => id !== String(currentUserId))
    )];

    const missing = allUserIds.filter((id) => !loadedUserIds.current.has(id));
    if (missing.length === 0) return;

    missing.forEach((id) => loadedUserIds.current.add(id));

    const results = await Promise.all(
      missing.map((id) =>
        userService.getUserById(id)
          .then((res) => ({ id, data: res.data }))
          .catch(() => { loadedUserIds.current.delete(id); return null; })
      )
    );

    const next = {};
    results.forEach((r) => { if (r) next[r.id] = r.data; });
    if (Object.keys(next).length > 0) {
      setUsers((prev) => ({ ...prev, ...next }));
    }
  }, [currentUserId]);

  return { users, setUsers, enrichConversations };
};

export default useConversationEnrichment;
